/**
 * THE THREE READ-ONLY VIEWS over the assignment plan — pure. No React, no I/O.
 *
 *   coverageFor()   what each rep owns, and DELIBERATELY NO PERCENTAGE
 *   queueFor()      every body with no team, reconciling to the universe
 *   lookAheadFor()  each rep's next 90 days, with date provenance intact
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ WHY COVERAGE SHIPS WITH NO PERCENTAGE.
 *
 * "How many of your championships have you touched in 30 days" needs an
 * actor-attributed event log. `effort_events` is the right table and is not
 * populated well enough to answer it, measured against the backend:
 *
 *   · 8 of the 17 event types written anywhere pass NO `actor` and normalize to
 *     SQL NULL (assignment.change, assignment.blocked, champion.repair,
 *     champion.blocked, journey.advance, journey.blocked, journey.unconfirmed,
 *     journey.approval). `rollupEffort` buckets them as '(unattributed)'.
 *   · Four kinds of real rep work file no row at all: a champion hunt
 *     (champion-hunt.js never imports effort.js), a pursuit-status change
 *     (pursuit-write.js imports appendLedgerEntry and not logEffortEvent), a
 *     crown through /api/champion-write, and an outreach approval.
 *   · Batch sentinels (`record_id = 'batch:...'`) corrupt count(DISTINCT record_id).
 *   · `actor` is a portal user id; `owner_at_time` is an sr_assigned_rep display
 *     name or a ClickUp username. They do not reconcile.
 *
 * So the only per-record actor-bearing writes are champion resolutions and
 * outreach stagings, and the query would UNDERCOUNT a rep who is prospecting hard
 * without running /api/champion-resolve. An undercount that makes working reps
 * look untouched is a false performance signal about real people, which is worse
 * than an absent number. `hs_lastmodifieddate` is no alternative: 4,845 records
 * share one day, so every rep reads ~71% touched regardless of what they did.
 *
 * `coverageFor()` therefore returns `touchedPct: null` and `touchedWhy` naming the
 * reason, and a surface that renders it must print the reason rather than a zero.
 * ─────────────────────────────────────────────────────────────────────────
 */

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())
const BLANK = /^(unassigned|unknown|none|null|undefined|n\/?a|-|—)$/i

/** The owner as the write sees it. '' when nobody is on it. */
export function ownerOf(record) {
  for (const f of ['sr_assigned_rep', 'sr_lead_rep', 'sr_support_rep_1']) {
    const v = S(record?.[f])
    if (v && !BLANK.test(v)) return v
  }
  return ''
}

export const isRetired = (record) => /^RETIRED\s*-\s*/i.test(S(record?.championship_name))

/**
 * Segments that belong to a DIFFERENT BOOK, not to this breakdown.
 *
 * ⚠ THIS LIST IS THE DIFFERENCE BETWEEN A QUEUE AND NOISE. 3,538 records sit
 * under cheer, dance, band, Canada, motorsports, rodeo, equestrian and pro
 * bodies. `rep_proficiency.json` already routes cheer and all-star hard to
 * Jennifer Loveday-Donovan. Labelling them "unassigned" would cry wolf on 3,538
 * rows and the queue would never be read again. They need a book, not a rep.
 */
export const OUT_OF_SCOPE_SEGMENTS = new Set([
  'All-Star', 'Dance', 'Band/Marching Arts', 'Canada', 'Motorsports', 'Equestrian',
  'Rodeo', 'Club Sport', 'Club', 'Professional', 'Semi-Pro', 'Youth/Travel Sports',
  'Esports', 'Pro Individual/Tour', 'Cheer/Dance (College)', 'Robotics',
  'Action Sports', 'Combat Sports', 'Adaptive/Other', 'Academic/CTE',
  'International (reference)',
])

/* ---- 1. THE QUEUE -------------------------------------------------------- */

/**
 * Every body, bucketed, with the counts that make the total reconcile.
 *
 * FOUR buckets because they need four different actions, and a single
 * "unassigned" list would collapse them:
 *
 *   covered        a team is on it
 *   inScopeNoRep   THE QUEUE. The breakdown should cover it and does not.
 *   outOfScope     a different book entirely; needs a book, not a rep
 *   noBody         no governing body on the record at all
 *
 * @returns { buckets, totals, reconciled }  `reconciled` is the assertion: the
 *   four bucket totals sum to the universe. A view that renders this must say so,
 *   because a queue that can silently drop a body is not a queue.
 */
export function queueFor(records, ownersByBody) {
  const owners = ownersByBody instanceof Map ? ownersByBody : new Map(Object.entries(ownersByBody || {}))
  const byBody = new Map()
  let noBody = 0

  for (const r of records || []) {
    const body = S(r.sr_conference)
    if (!body || body === 'Unspecified') { noBody++; continue }
    if (!byBody.has(body)) byBody.set(body, { body, count: 0, segments: new Map(), states: new Set() })
    const e = byBody.get(body)
    e.count++
    const sg = S(r.sr_segment) || '?'
    e.segments.set(sg, (e.segments.get(sg) || 0) + 1)
    const st = S(r.sr_state_location)
    if (st) e.states.add(st)
  }

  const covered = [], inScopeNoRep = [], outOfScope = []
  for (const e of byBody.values()) {
    const topSeg = [...e.segments.entries()].sort((a, b) => b[1] - a[1])[0][0]
    const row = { body: e.body, count: e.count, segment: topSeg, states: [...e.states], reps: owners.get(e.body) || null }
    if (row.reps?.length) covered.push(row)
    else if (OUT_OF_SCOPE_SEGMENTS.has(topSeg)) outOfScope.push({ ...row, why: `${topSeg} is a different line` })
    else inScopeNoRep.push({ ...row, why: 'the 2026-2027 breakdown names no rep for this body' })
  }

  const sum = (a) => a.reduce((s, x) => s + x.count, 0)
  const totals = {
    covered: sum(covered), inScopeNoRep: sum(inScopeNoRep),
    outOfScope: sum(outOfScope), noBody,
    universe: (records || []).length,
  }
  totals.accounted = totals.covered + totals.inScopeNoRep + totals.outOfScope + totals.noBody
  const byCount = (a, b) => b.count - a.count
  return {
    buckets: {
      covered: covered.sort(byCount),
      inScopeNoRep: inScopeNoRep.sort(byCount),
      outOfScope: outOfScope.sort(byCount),
    },
    totals,
    /* The property, not a claim: if this is false the view must say RECONCILIATION
     * FAILED and name the shortfall rather than printing the buckets as if whole. */
    reconciled: totals.accounted === totals.universe,
    shortfall: totals.universe - totals.accounted,
  }
}

/* ---- 2. COVERAGE, WITHOUT A PERCENTAGE ---------------------------------- */

export const TOUCHED_UNAVAILABLE =
  'Not measurable yet. 8 of 17 effort event types record no actor, and a champion '
  + 'hunt, a pursuit-status change and a /api/champion-write crown file no event at '
  + 'all, so this would undercount a rep who is working. It starts the day the '
  + 'attribution fix ships.'

/**
 * What each rep owns. Counts and next actions, never a coverage percentage.
 *
 * `touchedPct` is ALWAYS null and `touchedWhy` always states why. It is returned
 * as a field rather than omitted so a surface cannot accidentally render `0%` from
 * a missing key, and so the reason travels with the number's absence.
 */
export function coverageFor(records, { now = Date.now() } = {}) {
  const byRep = new Map()
  const bump = (rep, k) => {
    if (!byRep.has(rep)) {
      byRep.set(rep, {
        rep, total: 0, crowned: 0, toPursue: 0, noDate: 0, next90: 0, concluded: 0,
        touchedPct: null, touchedWhy: TOUCHED_UNAVAILABLE,
      })
    }
    byRep.get(rep)[k]++
  }
  const DAY = 864e5
  for (const r of records || []) {
    if (isRetired(r)) continue
    const rep = ownerOf(r)
    if (!rep) continue
    bump(rep, 'total')
    if (S(r.current_champion)) bump(rep, 'crowned')
    if (S(r.sr_pursuit_status) === 'To Pursue') bump(rep, 'toPursue')
    const d = effectiveDateOf(r)
    if (!d.at) bump(rep, 'noDate')
    else if (d.at >= now && d.at <= now + 90 * DAY) bump(rep, 'next90')
    else if (d.at < now && d.confidence === 'confirmed') bump(rep, 'concluded')
  }
  return [...byRep.values()].sort((a, b) => b.total - a.total)
}

/* ---- 3. THE LOOK-AHEAD, WITH DATE PROVENANCE INTACT --------------------- */

/**
 * The effective date AND where it came from.
 *
 * ⚠ A PROJECTED DATE MUST NEVER RENDER AS CONFIRMED, and this is the field order
 * that makes that true. `sr_date_status` outranks the field the date sits in:
 * 9,965 records carry status Projected while holding a real-looking
 * `sr_event_date`, and reading the date without the status is what would put
 * 9,965 guesses on a rep's calendar as deadlines.
 *
 * Returns `confidence` in { confirmed, projected, none } and `basis` naming the
 * property the value came from, so a surface can label rather than imply.
 */
export function effectiveDateOf(record) {
  const status = S(record?.sr_date_status)
  const parse = (v) => { const t = Date.parse(S(v)); return Number.isFinite(t) ? t : null }
  const confirmed = parse(record?.sr_confirmed_date)
  const event = parse(record?.sr_event_date)
  const projected = parse(record?.sr_projected_date)

  /* The record's own status outranks the field, and it only ever DOWNGRADES. */
  const saysProjected = status === 'Projected' || status === 'PENDING'

  if (confirmed != null && !saysProjected) return { at: confirmed, confidence: 'confirmed', basis: 'sr_confirmed_date' }
  if (confirmed != null) return { at: confirmed, confidence: 'projected', basis: 'sr_confirmed_date, but sr_date_status says Projected' }
  if (event != null) return { at: event, confidence: saysProjected || status !== 'Confirmed' ? 'projected' : 'confirmed', basis: 'sr_event_date' }
  if (projected != null) return { at: projected, confidence: 'projected', basis: 'sr_projected_date' }
  return { at: null, confidence: 'none', basis: '' }
}

/** Only a confirmed date may be shown to a customer or treated as a deadline. */
export const dateIsTrusted = (record) => effectiveDateOf(record).confidence === 'confirmed'

/**
 * One rep's next 90 days, soonest first.
 *
 * Every row carries `confidence` and `basis`, and `noPriorBusiness` flags a
 * championship at a school SR has never sold to, which is the row that needs the
 * longest runway.
 *
 * @param priorBusiness  Set of school names SR has prior business with
 */
export function lookAheadFor(records, rep, { now = Date.now(), days = 90, priorBusiness } = {}) {
  const DAY = 864e5
  const out = []
  for (const r of records || []) {
    if (isRetired(r) || ownerOf(r) !== rep) continue
    const d = effectiveDateOf(r)
    if (d.at == null || d.at < now || d.at > now + days * DAY) continue
    const school = S(r.sr_winning_school)
    out.push({
      id: r.id,
      name: S(r.championship_name),
      body: S(r.sr_conference),
      sport: S(r.sr_sport),
      state: S(r.sr_state_location),
      at: d.at,
      daysOut: Math.round((d.at - now) / DAY),
      /* ⚠ Carried, never collapsed. A surface that drops these two fields is the
       * bug this function exists to prevent. */
      confidence: d.confidence,
      basis: d.basis,
      /* null, not false, when there is nothing to check against: "we have no
       * history here" and "we could not look" are different answers. */
      noPriorBusiness: priorBusiness ? (school ? !priorBusiness.has(school) : null) : null,
      school,
    })
  }
  return out.sort((a, b) => a.at - b.at)
}

/**
 * The look-ahead's own honesty report: how much of it is projected.
 * A view showing 90 days of mostly-projected dates must say so up front.
 */
export function lookAheadProvenance(rows) {
  const confirmed = (rows || []).filter((r) => r.confidence === 'confirmed').length
  const total = (rows || []).length
  return {
    total,
    confirmed,
    projected: total - confirmed,
    mostlyProjected: total > 0 && confirmed / total < 0.5,
    note: total === 0 ? '' : confirmed / total < 0.5
      ? `${total - confirmed} of ${total} of these dates are PROJECTED, not confirmed. Treat them as a sequence, not a calendar.`
      : `${confirmed} of ${total} are confirmed dates.`,
  }
}
